import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useMotionTemplate } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MOSAIC_PROJECT } from "@/data";

export const MosaicProjectCard = () => {
    const video = useRef(null);
    const requested = useRef(false);
    const [playing, setPlaying] = useState(false);
    const [unavailable, setUnavailable] = useState(false);
    const pointerX = useMotionValue(0);
    const pointerY = useMotionValue(0);
    const radius = useMotionValue(600);
    const smoothX = useSpring(pointerX, { stiffness: 450, damping: 40, mass: 0.45 });
    const smoothY = useSpring(pointerY, { stiffness: 450, damping: 40, mass: 0.45 });
    const maskX = useMotionTemplate`${smoothX}px`;
    const maskY = useMotionTemplate`${smoothY}px`;
    const maskRadius = useMotionTemplate`${radius}px`;
    const trackPointer = (event, snap = false) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const x = typeof event.clientX === "number" ? event.clientX - bounds.left : bounds.width / 2;
        const y = typeof event.clientY === "number" ? event.clientY - bounds.top : bounds.height / 2;
        pointerX.set(x);
        pointerY.set(y);
        // Fixed 1200px diameter on wide images; only reduce it on narrow screens.
        radius.set(Math.min(600, bounds.width * 0.8));
        if (snap) {
            smoothX.jump(x);
            smoothY.jump(y);
        }
    };
    const start = () => {
        requested.current = true;
        if (!video.current || unavailable) return;
        video.current.play().then(() => {
            if (requested.current) setPlaying(true);
            else video.current?.pause();
        }).catch(() => setPlaying(false));
    };
    const stop = () => {
        requested.current = false;
        setPlaying(false);
        // Keep the video moving until the opacity transition finishes.
        if (!playing) video.current?.pause();
    };

    return (
        <motion.article data-testid="project-mosaic-castle" initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="group grid items-center gap-8 border-b border-[#121211]/10 py-12 lg:grid-cols-12 lg:py-16">
            <div className="lg:order-2 lg:col-span-7">
                <div
                    data-testid="mosaic-project-media"
                    data-preview-state={unavailable ? "unavailable" : playing ? "playing" : "idle"}
                    tabIndex={0}
                    role="group"
                    aria-label="Mosaic Mansion drone video preview"
                    onPointerEnter={(event) => {
                        if (event.pointerType !== "touch") { trackPointer(event, true); start(); }
                    }}
                    onPointerMove={(event) => { if (requested.current) trackPointer(event); }}
                    onPointerLeave={stop}
                    onPointerDown={(event) => {
                        if (event.pointerType === "touch") { trackPointer(event, true); start(); }
                    }}
                    onPointerUp={(event) => { if (event.pointerType === "touch") stop(); }}
                    onPointerCancel={stop}
                    onFocus={(event) => {
                        if (!requested.current) { trackPointer(event, true); start(); }
                    }}
                    onBlur={stop}
                    className="relative aspect-[16/10] cursor-default overflow-hidden outline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#121211]"
                >
                    <img src={MOSAIC_PROJECT.image} alt="Mosaic Mansion in the Hollywood Hills" loading="lazy" data-testid="mosaic-project-image" className="h-full w-full object-cover" />
                    <motion.video
                        ref={video}
                        src={MOSAIC_PROJECT.video}
                        muted
                        loop
                        playsInline
                        preload="none"
                        aria-hidden="true"
                        onError={() => { setUnavailable(true); setPlaying(false); }}
                        onTransitionEnd={(event) => {
                            if (event.propertyName === "opacity" && !requested.current) video.current?.pause();
                        }}
                        data-testid="mosaic-project-video"
                        className={`mosaic-drone-overlay ${playing ? "opacity-100" : "opacity-0"}`}
                        style={{ "--reveal-x": maskX, "--reveal-y": maskY, "--reveal-radius": maskRadius }}
                    />
                </div>
            </div>
            <div className="min-w-0 lg:order-1 lg:col-span-5 lg:text-right">
                <h3 data-testid="mosaic-project-title" className="font-display text-3xl font-light leading-tight text-[#121211] sm:text-4xl">{MOSAIC_PROJECT.title}</h3>
                <p data-testid="mosaic-project-category" className="mt-3 font-mono2 text-[10px] uppercase leading-relaxed tracking-[0.3em] text-[#6B6862]">{MOSAIC_PROJECT.category}</p>
                <p data-testid="mosaic-project-description" className="mt-3 text-sm font-light leading-relaxed text-[#121211]/70">{MOSAIC_PROJECT.description}</p>
                <div className="mt-6 flex flex-wrap gap-3 lg:justify-end">
                    <a data-testid="mosaic-project-instagram-link" data-interactive href={MOSAIC_PROJECT.instagramUrl} target="_self" className="project-cta inline-flex items-center gap-3 rounded-full border border-[#121211] bg-[#121211] px-5 py-3 font-mono2 text-[10px] uppercase tracking-[0.16em] text-[#FBF9F5]">
                        Instagram <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0" />
                    </a>
                    <a data-testid="mosaic-project-tour-link" data-interactive href={MOSAIC_PROJECT.tourUrl} target="_self" className="project-cta inline-flex items-center gap-3 rounded-full border border-[#121211]/30 px-5 py-3 font-mono2 text-[10px] uppercase tracking-[0.16em] text-[#121211]">
                        Visit 360° Tour <ArrowUpRight aria-hidden="true" className="h-4 w-4 shrink-0" />
                    </a>
                </div>
            </div>
        </motion.article>
    );
};